import _ from "underscore";
import { TFieldIdClass, TFieldDefClass } from "../types/api/APIClassTypes";
import { ObjectSearch } from "../models/ObjectSearch";
import { TSelectedCategories } from "../types/models/TSearchSettings";

const createReservationGroupNameFromTrack = (track: TrackVariation) => {
    console.log(track);
    const firstActivity = track.activities[0];
    return `${firstActivity.primaryObject}-${firstActivity.track}`;
};

// Checks the categories object, returns all field ids for which matchAll should be active
// If fieldDefs are provided, also filters out any fields which do not allow multiple values
const getMatchAllIds = (categories, fieldDefs?) => {
    const matchAllIds: number[] = [];

    Object.keys(categories).forEach((key) => {
        const values = categories[key];
        let matchAll = false;
        if (values.length > 1) {
            matchAll = _.every(values, (vl) => vl.length === 1);
        }
        const fieldId = parseInt(key, 10);
        if (fieldDefs) {
            const def = _.find(fieldDefs, (df) => df.id === fieldId);
            if (def && !def.multiple) {
                matchAll = false;
            }
        }
        if (matchAll) {
            matchAllIds.push(fieldId);
        }
    });
    return matchAllIds;
};

const getCategoryValues = (
    searchCriteria,
    fields: TFieldIdClass[],
    fieldDefs: TFieldDefClass[]
) => {
    const categoryValues = {};
    const removedValues: string[] = [];
    searchCriteria.categories.forEach((category) => {
        const field = fields.find((f) => f && f.extid === category.id);
        if (!field) {
            return;
        }
        const def = _.find(fieldDefs, (df) => df.id === field.id);
        if (category && Array.isArray(category.values)) {
            const finalValues = category.values
                .flat()
                .map((val) => val.trim())
                .filter((val) => {
                    const exists = def?.categories ? def.categories.indexOf(val) !== -1 : false;
                    if (!exists) {
                        removedValues.push(val);
                        return false;
                    }
                    return true;
                });
            if (finalValues.length > 0) {
                if (!categoryValues[field.id]) {
                    categoryValues[field.id] = [finalValues];
                } else {
                    categoryValues[field.id].push(finalValues);
                }
            }
        }
    });
    const matchAllIds = getMatchAllIds(categoryValues, fieldDefs);

    return { categoryValues, matchAllIds };
};

/* Returns true if the current value obj value is of type object  */
const isObject = (obj) => obj.type === "object" && _.isArray(obj.value);
/* Returns true if the current value obj value is of type filter  */
const isFilter = (obj) => obj.type === "object" && obj.value?.categories != null;

const isField = (obj) => obj.type === "field";

const isObjectOrFilter = (value) => isObject(value) || isFilter(value);

const isSameValueValue = (firstValue, secondValue) => {
    if (firstValue.categories && secondValue.categories) {
        return _.isEqual(firstValue.categories, secondValue.categories);
    }
    return _.isEqual(firstValue, secondValue);
};

export const isSameValue = (firstValue, secondValue) => {
    return (
        firstValue.type === secondValue.type &&
        firstValue.extId === secondValue.extId &&
        isSameValueValue(firstValue.value, secondValue.value)
    );
};

const isSameVariation = (firstValues, secondValues) => {
    // Can there be values which are not otherwise taken into account, making this check false?
    if (firstValues.length !== secondValues.length) {
        return false;
    }
    let isSame = true;
    // We shouldn't assume the same order, right?
    firstValues.forEach((firstValue) => {
        let existsInBoth = false;
        secondValues.forEach((secondValue) => {
            if (isSameValue(firstValue, secondValue)) {
                existsInBoth = true;
            }
        });
        if (!existsInBoth) {
            isSame = false;
        }
    });
    return isSame;
};

const getTrackData = (activities) => {
    // All activities in the track should already share all data
    // This is set up - using splitToVariations below - when data is first passed down from AM,
    // in requestManuallyScheduleActivities
    const commonObjects: any[] = [];
    const commonFilters: any[] = [];
    const commonFields: any[] = [];
    const firstActivity = activities[0];

    firstActivity.values
        .filter((value) => isObjectOrFilter(value))
        .forEach((object) => {
            if (isObject(object)) {
                object.value.forEach((val) => {
                    commonObjects.push(Object.assign({}, object, { value: [val] }));
                });
            } else {
                commonFilters.push(object);
            }
        });

    firstActivity.values
        .filter((value) => isField(value))
        .forEach((field) => commonFields.push(field));

    return {
        objects: commonObjects,
        filters: commonFilters,
        fields: commonFields,
    };
};

const getVariation = (activities) => {
    const variation: any[] = [];
    let workingSet = [...activities];
    let remainingActivities: any[] = [];
    let mainActivity = workingSet.pop();
    variation.push(mainActivity);
    workingSet.forEach((activity) => {
        if (isSameVariation(mainActivity.values, activity.values)) {
            variation.push(activity);
        } else {
            remainingActivities.push(activity);
        }
    });
    return { variation, remainingActivities };
};

const getAllVariations = (variations, activities) => {
    let { variation, remainingActivities } = getVariation(activities);
    variations.push(variation);
    if (remainingActivities.length > 0) {
        return getAllVariations(variations, remainingActivities);
    }
    return variations;
};

export type TrackVariation = {
    parentIndex: string;
    variationIndex: number;
    activities: any[];
};

export type TrackVariationSet = { [key: string]: TrackVariation };

const splitToVariations = (tracks): TrackVariationSet => {
    const tracksWithVariations: TrackVariationSet = {};
    Object.keys(tracks).forEach((trackKey) => {
        let activities = tracks[trackKey];
        const variations = getAllVariations([], activities);
        variations.forEach((variation, index) => {
            tracksWithVariations[`${trackKey}:${index + 1}`] = {
                parentIndex: trackKey,
                variationIndex: index,
                activities: variation,
            };
        });
    });
    return tracksWithVariations;
};

const isReservationId = (activityReservationId: number | undefined): boolean => {
    return activityReservationId != null;
};

const getSelectedCategories = (objectSearch: ObjectSearch): TSelectedCategories => {
    const selectedCategories: TSelectedCategories = {};
    // Advanced settings reaching us from elsewhere may be a partial object
    const { selectedCategories: categoriesByFieldId = {}, matchAll = [] } =
        objectSearch.advancedSettings;
    Object.keys(categoriesByFieldId).forEach((fieldId) => {
        const intId = parseInt(fieldId, 10);
        const field = _.find(objectSearch.allFields, (f) => f.id === intId);
        if (!field) {
            return;
        }
        const values = categoriesByFieldId[fieldId].flat();
        const isMatchAll = matchAll.indexOf(intId) !== -1;
        selectedCategories[field.extid] = isMatchAll ? values.map((value) => [value]) : values;
    });

    return selectedCategories;
};

export default {
    createReservationGroupNameFromTrack,
    getMatchAllIds,
    getCategoryValues,
    getTrackData,
    splitToVariations,
    isSameValue,
    isReservationId,
    getSelectedCategories,
};
